import { useCallback, useMemo } from 'react';
import { formatNumber, useI18n } from '@udemy/i18n';
import { SubscriptionPlanProductType } from '@udemy/subscription-graphql';
import { udLink } from '@udemy/ud-data';
import { useGwgEnrollmentData } from '../../../contexts/gwg-enrollment-context';
import { isCourseCompleted, useGwgCertificateUrl } from './use-gwg-certificate-url';
export let GwgCarouselCtaState = /*#__PURE__*/function (GwgCarouselCtaState) {
  GwgCarouselCtaState["NotSubscribed"] = "notSubscribed";
  GwgCarouselCtaState["NotEnrolled"] = "notEnrolled";
  GwgCarouselCtaState["EnrolledNotStarted"] = "enrolledNotStarted";
  GwgCarouselCtaState["EnrolledInProgress"] = "enrolledInProgress";
  GwgCarouselCtaState["Completed"] = "completed";
  return GwgCarouselCtaState;
}({});
/**
 * Encapsulates course extraction + selection logic.
 * This keeps the component render function small and easy to read.
 */
export function useGwgCarouselCourses(accelerator) {
  const courses = useMemo(() => accelerator.curriculum.sections.map(section => section.buyable), [accelerator.curriculum.sections]);
  const totalCourses = courses.length;
  const currentCourseIndex = useMemo(() => {
    if (!courses.length) {
      return 0;
    }

    // Build a list with derived access timestamps so we can select the most relevant course.
    const indexedCourses = courses.map((course, index) => ({
      course,
      index,
      lastAccessedTime: getCourseLastAccessedTime(course)
    }));
    const incompleteCourses = indexedCourses.filter(({
      course
    }) => !isCourseCompleted(course));
    const candidateCourses = incompleteCourses.length ? incompleteCourses : indexedCourses;
    const recentlyAccessedCourses = candidateCourses.filter(({
      lastAccessedTime
    }) => typeof lastAccessedTime === 'number').sort((left, right) => {
      var _right$lastAccessedTi, _left$lastAccessedTim;
      return ((_right$lastAccessedTi = right.lastAccessedTime) !== null && _right$lastAccessedTi !== void 0 ? _right$lastAccessedTi : 0) - ((_left$lastAccessedTim = left.lastAccessedTime) !== null && _left$lastAccessedTim !== void 0 ? _left$lastAccessedTim : 0);
    });
    return recentlyAccessedCourses.length ? recentlyAccessedCourses[0].index : candidateCourses[0].index;
  }, [courses]);
  return {
    courses,
    totalCourses,
    currentCourseIndex,
    currentCourse: courses[currentCourseIndex]
  };
}
export function useGwgCarouselEnrollment(accelerator, courses) {
  var _ref, _enrollmentFromContex, _enrollmentFromContex2;
  // Try to get enrollment data from context (preferred method)
  const enrollmentFromContext = useGwgEnrollmentData(accelerator.numericId);

  // Fallback to accelerator prop data for backwards compatibility
  const hasEnrollmentData = courses.some(course => course.userEnrollment);
  const isPathEnrolled = (_ref = (_enrollmentFromContex = enrollmentFromContext === null || enrollmentFromContext === void 0 ? void 0 : enrollmentFromContext.isEnrolled) !== null && _enrollmentFromContex !== void 0 ? _enrollmentFromContex : accelerator.isEnrolled) !== null && _ref !== void 0 ? _ref : hasEnrollmentData;
  const completionPercentage = (_enrollmentFromContex2 = enrollmentFromContext === null || enrollmentFromContext === void 0 ? void 0 : enrollmentFromContext.completionPercentage) !== null && _enrollmentFromContex2 !== void 0 ? _enrollmentFromContex2 : accelerator.completionPercentage;
  const hasCompletionPercentage = completionPercentage !== undefined && completionPercentage !== null;
  const isPathCompleted = hasCompletionPercentage ? completionPercentage >= 100 : courses.length > 0 && courses.every(isCourseCompleted);
  const hasStartedAnyCourse = courses.some(hasCourseProgress);
  return {
    isPathEnrolled,
    isPathCompleted,
    hasStartedAnyCourse
  };
}
/**
 * Encapsulates CTA copy + link selection logic.
 * This keeps state machine logic out of the JSX tree for readability.
 */
export function useGwgCarouselCta({
  accelerator,
  currentlySubscribedPlan,
  totalCourses,
  currentCourseIndex,
  currentCourse,
  isPathEnrolled,
  isPathCompleted,
  hasStartedAnyCourse
}) {
  const {
    gettext,
    interpolate,
    locale
  } = useI18n();
  const isGwgSubscriber = useMemo(() => {
    return (currentlySubscribedPlan === null || currentlySubscribedPlan === void 0 ? void 0 : currentlySubscribedPlan.productType) === SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle || (currentlySubscribedPlan === null || currentlySubscribedPlan === void 0 ? void 0 : currentlySubscribedPlan.productType) === SubscriptionPlanProductType.GrowWithGoogle;
  }, [currentlySubscribedPlan === null || currentlySubscribedPlan === void 0 ? void 0 : currentlySubscribedPlan.productType]);
  const shouldFetchCertificateUrl = useMemo(() => isGwgSubscriber && isPathCompleted, [isGwgSubscriber, isPathCompleted]);
  const {
    certificateUrl
  } = useGwgCertificateUrl({
    accelerator,
    enabled: shouldFetchCertificateUrl
  });
  const ctaState = useMemo(() => {
    if (!isGwgSubscriber) {
      return GwgCarouselCtaState.NotSubscribed;
    }
    if (!isPathEnrolled) {
      return GwgCarouselCtaState.NotEnrolled;
    }
    if (isPathCompleted) {
      return GwgCarouselCtaState.Completed;
    }
    if (hasStartedAnyCourse) {
      return GwgCarouselCtaState.EnrolledInProgress;
    }
    return GwgCarouselCtaState.EnrolledNotStarted;
  }, [hasStartedAnyCourse, isGwgSubscriber, isPathCompleted, isPathEnrolled]);
  const pathLandingUrl = useMemo(() => udLink.to(`/partner/google/${accelerator.slug}`), [accelerator.slug]);
  const currentCourseHref = useMemo(() => currentCourse ? getCourseTakingUrl(currentCourse) : pathLandingUrl, [currentCourse, pathLandingUrl]);
  const courseIndexLabel = useMemo(() => formatNumber(currentCourseIndex + 1, locale), [currentCourseIndex, locale]);
  const totalCoursesLabel = useMemo(() => formatNumber(totalCourses, locale), [locale, totalCourses]);
  const ctaCopyByState = useMemo(() => ({
    [GwgCarouselCtaState.NotSubscribed]: gettext('Learn more'),
    [GwgCarouselCtaState.NotEnrolled]: gettext('Enroll in path'),
    [GwgCarouselCtaState.EnrolledNotStarted]: interpolate(gettext('Start course %(courseIndex)s of %(totalCourses)s'), {
      courseIndex: courseIndexLabel,
      totalCourses: totalCoursesLabel
    }, true),
    [GwgCarouselCtaState.EnrolledInProgress]: interpolate(gettext('Resume course %(courseIndex)s of %(totalCourses)s'), {
      courseIndex: courseIndexLabel,
      totalCourses: totalCoursesLabel
    }, true),
    [GwgCarouselCtaState.Completed]: gettext('View certificate')
  }), [courseIndexLabel, gettext, interpolate, totalCoursesLabel]);
  const ctaHrefByState = useMemo(() => ({
    [GwgCarouselCtaState.NotSubscribed]: pathLandingUrl,
    [GwgCarouselCtaState.NotEnrolled]: pathLandingUrl,
    [GwgCarouselCtaState.EnrolledNotStarted]: currentCourseHref,
    [GwgCarouselCtaState.EnrolledInProgress]: currentCourseHref,
    [GwgCarouselCtaState.Completed]: certificateUrl !== null && certificateUrl !== void 0 ? certificateUrl : pathLandingUrl
  }), [certificateUrl, currentCourseHref, pathLandingUrl]);
  const courseCardHref = useCallback(course => {
    if (isGwgSubscriber && isPathEnrolled) {
      return getCourseTakingUrl(course);
    }
    return course.urlCourseLanding;
  }, [isGwgSubscriber, isPathEnrolled]);
  return {
    ctaState,
    ctaCopyByState,
    ctaHrefByState,
    pathLandingUrl,
    currentCourseHref,
    courseCardHref
  };
}
const hasCourseProgress = course => {
  var _course$userEnrollmen, _course$userEnrollmen2, _course$userEnrollmen3;
  const completionPercentage = (_course$userEnrollmen = (_course$userEnrollmen2 = course.userEnrollment) === null || _course$userEnrollmen2 === void 0 ? void 0 : _course$userEnrollmen2.completionPercentage) !== null && _course$userEnrollmen !== void 0 ? _course$userEnrollmen : 0;
  return completionPercentage > 0 || !!((_course$userEnrollmen3 = course.userEnrollment) !== null && _course$userEnrollmen3 !== void 0 && _course$userEnrollmen3.lastAccessedTime);
};
const getCourseLastAccessedTime = course => {
  var _course$userEnrollmen4;
  const lastAccessedTime = (_course$userEnrollmen4 = course.userEnrollment) === null || _course$userEnrollmen4 === void 0 ? void 0 : _course$userEnrollmen4.lastAccessedTime;
  if (!lastAccessedTime) {
    return undefined;
  }
  const parsedTime = Date.parse(lastAccessedTime);
  return Number.isNaN(parsedTime) ? undefined : parsedTime;
};
const getCourseTakingUrl = course => course.urlCourseTaking || course.urlCourseLanding;
//# sourceMappingURL=gwg-carousel.hooks.js.map